import React, { ReactNode } from "react";
import { Col, Container, Row, Button, Modal, Form } from "react-bootstrap";
import { Sun, Moon, GearFill, BookHalf } from "react-bootstrap-icons";
import { getEnabledWidgets, getWidgetsForCustomer } from "../../../config/analyticsWidgets";
import { getDefaultDateRange, getWidgetTheme } from "../../../utils/analytics";
import { createExportSchedule } from "../../../utils/Requests";
import { AnalyticsFilters, AnalyticsFilterOptions, ChartOptions, TrendlineType, TimezoneOption, TIMEZONE_OPTIONS, DEFAULT_FILTER_CONFIG, AnalyticsFilterConfig, DocsSidebarEntry } from "../../../types/analytics";
import AnalyticsFiltersComponent from "../../../components/analytics/AnalyticsFilters";
import { ExportButton, ScheduleExportModal, DocsSidebar } from "../../../components/analytics";
import { ExportContext, ExportableData, ExportScheduleRequest } from "../../../services/export";

/**
 * Analytics Playground page filter configuration
 * Uses standard presets with all filters and full group by functionality enabled
 */
const FILTER_CONFIG: AnalyticsFilterConfig = {
    ...DEFAULT_FILTER_CONFIG,
    datePresetStyle: "presets",
    defaultDatePreset: "W",
    showStartDate: true,
    showEndDate: true,
    showDeviceFilter: true,
    showBrandFilter: true,
    showAttributionFilter: true,
    enableGroupBy: {
        deviceType: true,
        brandId: true,
        attributionId: true
    },
    showApplyButton: true
};

// Reference the config to avoid unused variable warning
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _filterConfig = FILTER_CONFIG;

type AnalyticsProperties = {
    standalone?: boolean;
    groups: string[];
    username: string;
    customerConfig: CustomerConfig;
};

type AnalyticsState = {
    filters: AnalyticsFilters; // Current filter UI state
    appliedFilters: AnalyticsFilters; // Filters passed to widgets (only updates on Apply)
    filterOptions: AnalyticsFilterOptions;
    isDarkTheme: boolean;
    filterKey: number; // Used to trigger widget re-fetch
    showOptionsModal: boolean;
    chartOptions: ChartOptions; // Current options UI state
    appliedChartOptions: ChartOptions; // Options passed to widgets (only updates on Apply)
    timezone: TimezoneOption; // Selected timezone for date display
    showScheduleModal: boolean;
    sidebarOpen: boolean;
    sidebarEntries: DocsSidebarEntry[];
};

class AnalyticsPlayground extends React.Component<AnalyticsProperties, AnalyticsState> {
    private containerRef = React.createRef<HTMLDivElement>();
    private focusSizeRef = React.createRef<HTMLInputElement>();
    private focusSizeLabelRef = React.createRef<HTMLSpanElement>();

    constructor(props: AnalyticsProperties) {
        super(props);
        
        const { startDate, endDate } = getDefaultDateRange();
        
        const initialFilters: AnalyticsFilters = {
            startDate,
            endDate,
            attributionId: [],
            brandId: [],
            deviceType: []
        };
        
        this.state = {
            filters: initialFilters,
            appliedFilters: initialFilters, // Start with same initial values
            filterOptions: {
                attributionIds: [],
                brandIds: [],
                deviceTypes: []
            },
            isDarkTheme: true,
            filterKey: 0,
            showOptionsModal: false,
            chartOptions: {
                showTrendline: false,
                trendlineType: TrendlineType.MOVING_AVERAGE,
                focusSize: 10
            },
            appliedChartOptions: {
                showTrendline: false,
                trendlineType: TrendlineType.MOVING_AVERAGE,
                focusSize: 10
            },
            timezone: "local", // Default to browser timezone
            showScheduleModal: false,
            sidebarOpen: false,
            sidebarEntries: []
        };
    }

    private handleFilterChange = (filters: AnalyticsFilters): void => {
        this.setState({ filters });
    };

    private handleApplyFilters = (): void => {
        // Copy current filters to appliedFilters and increment key to trigger widget re-fetch
        this.setState(prevState => ({
            appliedFilters: { ...prevState.filters },
            filterKey: prevState.filterKey + 1
        }));
    };

    private toggleTheme = (): void => {
        this.setState(prevState => ({ isDarkTheme: !prevState.isDarkTheme }));
    };

    private toggleOptionsModal = (): void => {
        this.setState(prevState => ({ showOptionsModal: !prevState.showOptionsModal }));
    };

    private handleTrendlineToggle = (): void => {
        this.setState(prevState => {
            const newShowTrendline = !prevState.chartOptions.showTrendline;
            return {
                chartOptions: {
                    ...prevState.chartOptions,
                    showTrendline: newShowTrendline
                },
                appliedChartOptions: {
                    ...prevState.appliedChartOptions,
                    showTrendline: newShowTrendline
                }
            };
        });
    };

    private handleTrendlineTypeChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const newType = event.target.value as TrendlineType;
        this.setState(prevState => ({
            chartOptions: {
                ...prevState.chartOptions,
                trendlineType: newType
            },
            appliedChartOptions: {
                ...prevState.appliedChartOptions,
                trendlineType: newType
            }
        }));
    };

    private handleFocusSizeChange = (): void => {
        const value = parseInt(this.focusSizeRef.current?.value || "10", 10);
        this.setState(prevState => ({
            chartOptions: {
                ...prevState.chartOptions,
                focusSize: Math.min(30, Math.max(1, value))
            }
        }));
    };

    private handleFocusSizeInput = (): void => {
        // Update label in real-time without triggering React re-render
        if (this.focusSizeLabelRef.current && this.focusSizeRef.current) {
            this.focusSizeLabelRef.current.textContent = this.focusSizeRef.current.value;
        }
    };

    private hasOptionsChanges = (): boolean => {
        const { chartOptions, appliedChartOptions } = this.state;
        // Only focusSize requires Apply - trendline options apply immediately
        return chartOptions.focusSize !== appliedChartOptions.focusSize;
    };

    private applyOptionsChanges = (): void => {
        this.setState(prevState => {
            // Only increment filterKey (trigger API re-fetch) if focusSize changed
            // Trendline changes are purely client-side calculations
            const needsRefetch = prevState.chartOptions.focusSize !== prevState.appliedChartOptions.focusSize;
            return {
                appliedChartOptions: { ...prevState.chartOptions },
                filterKey: needsRefetch ? prevState.filterKey + 1 : prevState.filterKey
            };
        });
    };

    private handleTimezoneChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        this.setState({ timezone: event.target.value as TimezoneOption });
    };

    private getExportData = (): ExportableData[] => {
        // Analytics Playground shows dynamic widgets - for export purposes,
        // we provide the filter configuration for scheduled exports
        const { appliedFilters } = this.state;
        return [{
            title: "Analytics Playground",
            columns: [
                { key: "startDate", label: "Start Date" },
                { key: "endDate", label: "End Date" },
                { key: "filters", label: "Applied Filters" }
            ],
            rows: [{
                startDate: appliedFilters.startDate,
                endDate: appliedFilters.endDate,
                filters: JSON.stringify({
                    deviceType: appliedFilters.deviceType,
                    brandId: appliedFilters.brandId,
                    attributionId: appliedFilters.attributionId
                })
            }],
            metadata: {
                filters: appliedFilters,
                pageKey: "analytics_playground",
                customerSlug: this.props.customerConfig.slug
            }
        }];
    };

    private getExportContext = (): ExportContext => {
        const { appliedFilters } = this.state;
        return {
            pageTitle: "Analytics Playground",
            pageKey: "analytics_playground",
            customerSlug: this.props.customerConfig.slug,
            filters: appliedFilters,
            getData: this.getExportData,
            getContainerRef: () => this.containerRef.current
        };
    };

    private handleScheduleSave = async (schedule: ExportScheduleRequest & { id: string }): Promise<void> => {
        await createExportSchedule(this.props.customerConfig, schedule);
    };

    private toggleSidebar = (): void => {
        this.setState(prevState => ({ sidebarOpen: !prevState.sidebarOpen }));
    };

    private handleMetaLoaded = (entry: DocsSidebarEntry): void => {
        this.setState(prevState => ({
            sidebarEntries: [
                ...prevState.sidebarEntries.filter(e => e.widgetKey !== entry.widgetKey),
                entry
            ]
        }));
    };

    public render(): ReactNode {
        const { filters, appliedFilters, filterOptions, isDarkTheme, filterKey, showOptionsModal, chartOptions, appliedChartOptions, timezone, sidebarOpen, sidebarEntries } = this.state;
        const theme = getWidgetTheme(isDarkTheme);
        const hasOptionsChanges = this.hasOptionsChanges();

        // Get enabled widgets for this customer
        const enabledWidgetKeys = getEnabledWidgets(this.props.customerConfig);
        const widgets = getWidgetsForCustomer(enabledWidgetKeys, this.props.customerConfig.name);

        return (
            <div ref={this.containerRef} style={{ display: "flex", alignItems: "flex-start" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Container className="mw-100" key="analytics" style={{ position: "relative" }}>
                    <div data-export-ignore style={{ position: "absolute", right: "0px", top: "0px", zIndex: 1000, display: "flex", gap: "8px" }}>
                        <ExportButton
                            context={this.getExportContext()}
                            enabled={true}
                            scheduledEnabled={this.props.customerConfig.exportConfig?.scheduledEnabled !== false}
                            onScheduleClick={() => this.setState({ showScheduleModal: true })}
                        />
                        <Button
                            onClick={this.toggleOptionsModal}
                            style={{
                                backgroundColor: "transparent",
                                border: "none",
                                fontSize: "20px",
                                padding: "4px 8px"
                            }}
                            title="Chart Options"
                        >
                            <GearFill />
                        </Button>
                        <Button
                            onClick={this.toggleSidebar}
                            style={{
                                backgroundColor: "transparent",
                                border: "none",
                                fontSize: "20px",
                                padding: "4px 8px"
                            }}
                            title={sidebarOpen ? "Close Documentation" : "Open Documentation"}
                        >
                            <BookHalf />
                        </Button>
                        <Button
                            onClick={this.toggleTheme}
                            style={{
                                backgroundColor: "transparent",
                                border: "none",
                                fontSize: "24px",
                                padding: "4px 8px"
                            }}
                            title={isDarkTheme ? "Switch to Light Theme" : "Switch to Dark Theme"}
                        >
                            {isDarkTheme ? <Sun /> : <Moon />}
                        </Button>
                    </div>

                    {/* Options Modal */}
                    <Modal show={showOptionsModal} onHide={this.toggleOptionsModal}>
                        <Modal.Header closeButton>
                            <Modal.Title>Chart Options</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="switch"
                                        id="trendline-switch"
                                        label="Show Trendline"
                                        checked={chartOptions.showTrendline}
                                        onChange={this.handleTrendlineToggle}
                                    />
                                    <Form.Text className="text-muted">
                                        Display a trendline on bar charts to show the overall trend.
                                    </Form.Text>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Trendline Type</Form.Label>
                                    <Form.Control
                                        as="select"
                                        value={chartOptions.trendlineType}
                                        onChange={this.handleTrendlineTypeChange as any}
                                        disabled={!chartOptions.showTrendline}
                                        style={{ opacity: chartOptions.showTrendline ? 1 : 0.5 }}
                                    >
                                        <option value={TrendlineType.CONNECTED}>Connected (Data Points)</option>
                                        <option value={TrendlineType.SPLINE}>Spline (Cubic)</option>
                                        <option value={TrendlineType.MOVING_AVERAGE}>Moving Average</option>
                                        <option value={TrendlineType.EMA}>Exponential Moving Average</option>
                                        <option value={TrendlineType.LOGARITHMIC}>Logarithmic</option>
                                        <option value={TrendlineType.LOESS}>LOESS (Local Smoothing)</option>
                                        <option value={TrendlineType.POLYNOMIAL}>Polynomial (Degree 2)</option>
                                        <option value={TrendlineType.EXPONENTIAL}>Exponential</option>
                                        <option value={TrendlineType.LINEAR}>Linear Regression</option>
                                    </Form.Control>
                                    <Form.Text className="text-muted">
                                        Select the type of regression for the trendline.
                                    </Form.Text>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Group By Size: <span ref={this.focusSizeLabelRef}>{chartOptions.focusSize}</span></Form.Label>
                                    <Form.Control
                                        ref={this.focusSizeRef}
                                        type="range"
                                        min={1}
                                        max={30}
                                        defaultValue={chartOptions.focusSize}
                                        onInput={this.handleFocusSizeInput}
                                        onMouseUp={this.handleFocusSizeChange}
                                        onTouchEnd={this.handleFocusSizeChange}
                                    />
                                    <Form.Text className="text-muted">
                                        Number of values to show when grouping by a field (1-30). Additional values are combined into &quot;Other&quot;.
                                    </Form.Text>
                                </Form.Group>
                                <hr />
                                <Form.Group className="mb-3">
                                    <Form.Label>Timezone</Form.Label>
                                    <Form.Control
                                        as="select"
                                        value={timezone}
                                        onChange={this.handleTimezoneChange as any}
                                    >
                                        {TIMEZONE_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </Form.Control>
                                    <Form.Text className="text-muted">
                                        Display dates in the selected timezone. Data is stored in UTC.
                                    </Form.Text>
                                </Form.Group>
                            </Form>
                        </Modal.Body>
                        <Modal.Footer>
                            {hasOptionsChanges ? (
                                <Button variant="primary" onClick={this.applyOptionsChanges}>
                                    Apply Options
                                </Button>
                            ) : (
                                <Button variant="secondary" onClick={this.toggleOptionsModal}>
                                    Close
                                </Button>
                            )}
                        </Modal.Footer>
                    </Modal>

                    <Row>
                        <Col md style={{ textAlign: "center", marginTop: "24px" }}>
                            <h2>Analytics Playground</h2>
                        </Col>
                    </Row>
                    
                    <AnalyticsFiltersComponent
                        filters={filters}
                        filterOptions={filterOptions}
                        onFilterChange={this.handleFilterChange}
                        onApplyFilters={this.handleApplyFilters}
                        customerConfig={this.props.customerConfig}
                        enableGroupBy={{
                            deviceType: true,
                            brandId: true,
                            attributionId: true
                        }}
                    />

                    <Row style={{ paddingTop: "24px" }}>
                        <Col md={12}>
                            {enabledWidgetKeys.map(widgetKey => {
                                const widgetConfig = widgets[widgetKey];
                                if (!widgetConfig) return null;
                                
                                const WidgetComponent = widgetConfig.component;
                                
                                return (
                                    <WidgetComponent
                                        key={`${widgetKey}-${filterKey}`}
                                        customerConfig={this.props.customerConfig}
                                        username={this.props.username}
                                        groups={this.props.groups}
                                        filters={appliedFilters}
                                        isDarkTheme={isDarkTheme}
                                        theme={theme}
                                        chartOptions={appliedChartOptions}
                                        timezone={timezone}
                                        onMetaLoaded={this.handleMetaLoaded}
                                    />
                                );
                            })}
                        </Col>
                    </Row>
                    <ScheduleExportModal
                        show={this.state.showScheduleModal}
                        onHide={() => this.setState({ showScheduleModal: false })}
                        filters={appliedFilters}
                        pageKey="analytics_playground"
                        pageTitle="Analytics Playground"
                        customerSlug={this.props.customerConfig.slug}
                        editingSchedule={null}
                        onSave={this.handleScheduleSave}
                    />
                </Container>
              </div>
              <DocsSidebar isOpen={sidebarOpen} entries={sidebarEntries} />
            </div>
        );
    }
}

export default AnalyticsPlayground;
